import { useEffect, useMemo, useState } from 'react';
import { COOWN_PROJECT_START_YEAR } from '../../config/constants';
import apiClient from '../../utils/api';
import BrowseListedDateFilter from '../listings/BrowseListedDateFilter';
import { maskTaxIdLast4 } from '../../data/countries';
import { getUserAvatarUrl, getUserInitials } from '../../utils/properties';
import { buildAdminReviewEntries, formatAdminReviewerLabel, formatAdminReviewTimestamp } from '../../utils/submissions';
import AdminReviewLogPanel from '../forms/AdminReviewLogPanel';
import PlatformActivityChart from './PlatformActivityChart';
import PaginationBar from '../shared/PaginationBar';

const STATUS_FILTERS = [
  { key: 'pending', label: 'Pending review', icon: 'fa-hourglass-half' },
  { key: 'approved', label: 'Verified', icon: 'fa-check-circle' },
  { key: 'rejected', label: 'Rejected', icon: 'fa-times-circle' },
  { key: 'all', label: 'All users', icon: 'fa-users' },
];

const STATUS_METRICS = [
  { key: 'pending', label: 'Pending review' },
  { key: 'approved', label: 'Verified' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_CHART_COLORS = {
  pending: '#d4b06f',
  approved: '#28a745',
  rejected: '#dc3545',
};

const PAGE_SIZE = 10;

const statusBadgeClass = (status) => {
  if (status === 'approved') return 'status-badge status-approved';
  if (status === 'rejected') return 'status-badge status-rejected';
  return 'status-badge status-pending';
};

const statusLabel = (status) => {
  if (status === 'approved') return 'Verified';
  if (status === 'rejected') return 'Rejected';
  return 'Pending';
};

const formatTimestamp = (value) => {
  if (!value) return '—';
  const d = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const formatShortDate = (value) => {
  if (!value) return '—';
  const d = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const getDisplayName = (row) =>
  row.fullName || [row.firstName, row.lastName].filter(Boolean).join(' ') || 'Unknown';

const getInitials = (row) =>
  getDisplayName(row).split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

const formatIdType = (value) => String(value || 'ID').replace(/_/g, ' ');

const formatKycReviewer = (row) => {
  if (row.reviewerName && row.reviewerEmail) return `${row.reviewerName} (${row.reviewerEmail})`;
  if (row.reviewerName || row.reviewerEmail) return row.reviewerName || row.reviewerEmail;

  const latest = buildAdminReviewEntries({ adminReviewLog: row.adminReviewLog })[0];
  if (latest) return formatAdminReviewerLabel(latest);
  return 'Unknown admin';
};

const KycDetailModal = ({ row, busy, onClose, renderActions }) => {
  if (!row) return null;
  const displayName = getDisplayName(row);
  const avatarUrl = getUserAvatarUrl(row);

  return (
    <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && !busy && onClose?.()}>
      <div className="modal admin-kyc-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="kyc-detail-title">
        <div className="admin-kyc-modal__header">
          <div className="admin-kyc-modal__header-main">
            <div className="admin-kyc-modal__identity">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="admin-kyc-modal__avatar-img" />
              ) : (
                <div className="admin-kyc-modal__avatar" aria-hidden="true">{getInitials(row)}</div>
              )}
              <div>
                <span className={statusBadgeClass(row.status)}>{statusLabel(row.status)}</span>
                <h2 id="kyc-detail-title">{displayName}</h2>
                <p className="admin-kyc-modal__subtitle">
                  {row.email || '—'}
                  {row.isActive === false ? ' · Blocked' : ''}
                </p>
                <p className="admin-kyc-modal__subtitle">
                  {row.countryCode || '—'} · Submitted {formatTimestamp(row.submittedAt)} · {formatIdType(row.idType)}
                </p>
              </div>
            </div>
          </div>
          <button type="button" className="modal-close" disabled={busy} onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="modal-body admin-kyc-modal__body">
          {row.reviewedAt ? (
            <div className="admin-kyc-modal__review-summary">
              Last reviewed {formatAdminReviewTimestamp(row.reviewedAt)} by {formatKycReviewer(row)}
              {row.reviewNotes ? ` · ${row.reviewNotes}` : ''}
            </div>
          ) : null}

          <div className="admin-kyc-modal__details">
            <div><strong>User ID:</strong> {row.id}</div>
            <div><strong>DOB:</strong> {row.dob || '—'}</div>
            <div><strong>Tax/ID:</strong> {maskTaxIdLast4(row.taxIdLast4 || row.ssn)}</div>
            <div><strong>Address:</strong> {row.address || '—'}, {row.cityRegionPostal || row.cityStateZip || '—'}</div>
          </div>

          {row.idDocumentUrl ? (
            <div className="admin-kyc-modal__document">
              <label>ID document (admin only)</label>
              <a href={row.idDocumentUrl} target="_blank" rel="noopener noreferrer">
                <img src={row.idDocumentUrl} alt="ID document" />
              </a>
            </div>
          ) : null}

          <AdminReviewLogPanel
            sub={{ adminReviewLog: row.adminReviewLog }}
            hint="Every approve, reject, and re-open action is logged with the admin name and email."
          />

          <div className="admin-kyc-modal__actions">
            {renderActions(row)}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminKycReviewPanel = ({ addToast }) => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
    startIndex: 0,
    endIndex: 0,
  });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [chartType, setChartType] = useState('pie');
  const [kycDateMode, setKycDateMode] = useState('all');
  const [kycDateYear, setKycDateYear] = useState('');
  const [kycDateMonth, setKycDateMonth] = useState('');
  const [kycDateDay, setKycDateDay] = useState('');

  const chartMetrics = useMemo(() => ({
    pending: stats.pending || 0,
    approved: stats.approved || 0,
    rejected: stats.rejected || 0,
  }), [stats]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.listKYCForAdmin({
        status: statusFilter,
        search: searchQuery,
        page,
        limit: PAGE_SIZE,
        dateMode: kycDateMode,
        dateYear: kycDateYear,
        dateMonth: kycDateMonth,
        dateDay: kycDateDay,
      });
      const payload = res.data || {};
      setRows(Array.isArray(payload.items) ? payload.items : []);
      setStats(payload.stats || { pending: 0, approved: 0, rejected: 0, total: 0 });
      setPagination(payload.pagination || {
        page: 1,
        limit: PAGE_SIZE,
        total: 0,
        totalPages: 1,
        hasPrev: false,
        hasNext: false,
        startIndex: 0,
        endIndex: 0,
      });
    } catch (err) {
      addToast(err.message || 'Could not load KYC users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedId(null);
    load();
  }, [statusFilter, searchQuery, page, kycDateMode, kycDateYear, kycDateMonth, kycDateDay]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const handleReview = async (id, status, confirmMessage) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    const notes = status === 'rejected'
      ? window.prompt('Rejection notes (optional):', '') || ''
      : status === 'pending'
        ? window.prompt('Reason for re-opening review (optional):', '') || ''
        : '';

    setReviewingId(id);
    try {
      await apiClient.reviewKYC(id, status, notes);
      const messages = {
        approved: 'KYC approved — user is now verified.',
        rejected: 'KYC rejected.',
        pending: 'KYC moved back to pending review.',
      };
      addToast(messages[status] || 'KYC updated', status === 'approved' ? 'success' : 'info');
      await load();
      if (status !== 'pending' && statusFilter === 'pending') {
        setSelectedId(null);
      }
    } catch (err) {
      addToast(err.message || 'Review failed', 'error');
    } finally {
      setReviewingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiClient.downloadKYCExport({ status: statusFilter, search: searchQuery });
      addToast('KYC user CSV downloaded.', 'success');
    } catch (err) {
      addToast(err.message || 'CSV export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const statCount = (key) => {
    if (key === 'all') return stats.total || 0;
    return stats[key] || 0;
  };

  const selectedRow = rows.find((r) => r.id === selectedId) || null;

  const renderActions = (row) => {
    const busy = reviewingId === row.id;
    if (row.status === 'pending') {
      return (
        <>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => handleReview(row.id, 'approved')}>
            Approve
          </button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} disabled={busy} onClick={() => handleReview(row.id, 'rejected', 'Reject this KYC submission?')}>
            Reject
          </button>
        </>
      );
    }
    if (row.status === 'approved') {
      return (
        <>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={busy}
            onClick={() => handleReview(row.id, 'pending', 'Re-open this verified user for review? They will not be able to invest until approved again.')}
          >
            Re-open for review
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--danger)' }}
            disabled={busy}
            onClick={() => handleReview(row.id, 'rejected', 'Revoke verification for this user?')}
          >
            Revoke verification
          </button>
        </>
      );
    }
    return (
      <>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => handleReview(row.id, 'approved')}>
          Approve
        </button>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={busy}
          onClick={() => handleReview(row.id, 'pending', 'Move this user back to pending review?')}
        >
          Re-open for review
        </button>
      </>
    );
  };

  return (
    <div>
      <div className="admin-panel-head">
        <div>
          <h3><i className="fas fa-id-card"></i> KYC users</h3>
          <p className="admin-panel-sub">
            Review pending submissions, browse verified users, and re-open accounts if something needs another look. ID images are admin-only.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={handleExport} disabled={exporting || loading}>
            <i className={`fas fa-download${exporting ? ' fa-spin' : ''}`}></i> Download CSV
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            <i className={`fas fa-sync${loading ? ' fa-spin' : ''}`}></i> Refresh
          </button>
        </div>
      </div>

      <div className="platform-activity-layout admin-kyc-analytics">
        <div
          className="admin-chat-reports-status-filters"
          role="group"
          aria-label="Filter KYC users by status"
        >
          {STATUS_FILTERS.map((box) => (
            <button
              key={box.key}
              type="button"
              className={`admin-chat-reports-status-btn${statusFilter === box.key ? ' active' : ''}`}
              onClick={() => { setStatusFilter(box.key); setPage(1); }}
            >
              <span className="admin-chat-reports-status-btn__label">
                <i className={`fas ${box.icon}`} aria-hidden="true"></i>
                {box.label}
              </span>
              <span className="admin-chat-reports-status-btn__value">{statCount(box.key).toLocaleString()}</span>
            </button>
          ))}
        </div>
        <PlatformActivityChart
          metrics={chartMetrics}
          metricDefs={STATUS_METRICS}
          chartType={chartType}
          onChartTypeChange={setChartType}
          title="Users by status"
          emptyMessage="No KYC submissions yet."
          ariaLabel="KYC users status breakdown"
          colorMap={STATUS_CHART_COLORS}
        />
      </div>

      <div className="live-listings-toolbar" style={{ marginBottom: '1rem' }}>
        <div className="live-listings-toolbar__search">
          <div className="submission-queue-search">
            <div className="submission-queue-search-wrap">
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="text"
                inputMode="search"
                autoComplete="off"
                spellCheck={false}
                placeholder="Search name or email…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search KYC users"
              />
              {searchInput.trim() ? (
                <button
                  type="button"
                  className="submission-queue-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearchInput('')}
                >
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              ) : null}
            </div>
          </div>
        </div>
        <BrowseListedDateFilter
          variant="admin"
          useCalendarInputs
          fieldLabel="Submitted"
          yearMin={COOWN_PROJECT_START_YEAR}
          dateMode={kycDateMode}
          onDateModeChange={(mode) => { setKycDateMode(mode); setPage(1); }}
          listedYear={kycDateYear}
          onListedYearChange={(v) => { setKycDateYear(v); setPage(1); }}
          listedMonth={kycDateMonth}
          onListedMonthChange={(v) => { setKycDateMonth(v); setPage(1); }}
          listedDate={kycDateDay}
          onListedDateChange={(v) => { setKycDateDay(v); setPage(1); }}
          onClear={() => {
            setKycDateMode('all');
            setKycDateYear('');
            setKycDateMonth('');
            setKycDateDay('');
            setPage(1);
          }}
        />
      </div>

      <div className="admin-table-wrap">
        <table className="investment-table investment-table--kyc-users">
          <thead>
            <tr>
              <th>User</th>
              <th>Country</th>
              <th>Submitted</th>
              <th>ID type</th>
              <th>Status</th>
              <th>Reviewer</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  Loading KYC users…
                </td>
              </tr>
            ) : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  {searchQuery.trim()
                    ? 'No users match your search.'
                    : statusFilter === 'pending'
                      ? 'No pending KYC submissions.'
                      : statusFilter === 'approved'
                        ? 'No verified users yet.'
                        : statusFilter === 'rejected'
                          ? 'No rejected KYC submissions.'
                          : 'No KYC submissions yet.'}
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const avatarUrl = getUserAvatarUrl(row);
              return (
              <tr
                key={row.id}
                className={`admin-kyc-row${selectedId === row.id ? ' is-selected' : ''}`}
                onClick={() => setSelectedId(row.id)}
              >
                <td className="live-listing-deal-cell">
                  <div className="admin-registered-user-row__identity">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="admin-registered-user-row__avatar-img" />
                    ) : (
                      <div className="admin-registered-user-row__avatar" aria-hidden="true">{getUserInitials(row)}</div>
                    )}
                    <div className="admin-registered-user-row__text">
                      <div className="live-listing-deal-line">
                        <strong className="live-listing-deal-title">{getDisplayName(row)}</strong>
                      </div>
                      <div className="live-listing-deal-sub admin-kyc-row__email">
                        {row.email || '—'}
                        {row.isActive === false ? ' · Blocked' : ''}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{row.countryCode || '—'}</td>
                <td>{formatShortDate(row.submittedAt)}</td>
                <td>{formatIdType(row.idType)}</td>
                <td><span className={statusBadgeClass(row.status)}>{statusLabel(row.status)}</span></td>
                <td className="admin-kyc-row__email">{row.reviewerEmail || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm live-listing-manage-btn"
                    onClick={(e) => { e.stopPropagation(); setSelectedId(row.id); }}
                  >
                    Open
                  </button>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {rows.length > 0 ? (
        <PaginationBar
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          hasPrev={pagination.hasPrev}
          hasNext={pagination.hasNext}
          onPageChange={setPage}
          itemLabel="users"
          compact
        />
      ) : null}

      <KycDetailModal
        row={selectedRow}
        busy={Boolean(reviewingId)}
        onClose={() => setSelectedId(null)}
        renderActions={renderActions}
      />
    </div>
  );
};

export default AdminKycReviewPanel;
